import { createContext } from 'react';

export enum ThemeMode {
  AUTO = 'auto',
  LIGHT = 'light',
  DARK = 'dark',
}

const initThemeMode = getThemeMode();

export type ThemeContextValue = ReturnType<typeof useThemeContextValue>;

export const themeContext = createContext<ThemeContextValue>({} as never);

export function useThemeContextValue() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(initThemeMode);

  const isThemeMode = useCallback((mode: ThemeMode) => mode === themeMode, [themeMode]);

  const isDark = useMemo(() => isThemeMode(ThemeMode.DARK), [isThemeMode]);

  const value = useMemo(
    () => ({
      themeMode,
      setThemeMode,
      isThemeMode,
      isDark,
    }),
    [themeMode, isThemeMode, isDark]
  );

  // Set the theme mode based on the system preference
  useLayoutEffect(() => {
    const m = window.matchMedia('(prefers-color-scheme: dark)');

    if (themeMode === ThemeMode.AUTO) {
      m.addEventListener('change', handleThemeModeChange);
      m.dispatchEvent(new Event('change'));
    }

    function handleThemeModeChange() {
      const isDark = m.matches;
      setThemeMode(isDark ? ThemeMode.DARK : ThemeMode.LIGHT);
    }

    return () => {
      m.removeEventListener('change', handleThemeModeChange);
    };
  }, [themeMode]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = themeMode;
    setThemeMode(themeMode);
  }, [themeMode]);

  return value;
}

export function useThemeContext() {
  return useContext(themeContext);
}

export function getThemeMode() {
  return (localStorage.getItem('theme-mode') as ThemeMode) ?? ThemeMode.AUTO;
}

export function setThemeMode(mode: ThemeMode) {
  return localStorage.setItem('theme-mode', mode);
}
